import React, { useCallback, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { Button } from '../../../../../../../components/ui/button';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { getPresignedUrlToDownloadSpoolsbaseDoc } from '../../../../../../../redux/Spoolsbase/saga';
import {
  formatSpoolStepDocumentDate,
  type SpoolStepPersistedDoc,
} from './spoolStepPayloadHelpers';

export interface SpoolStepSavedDocumentsListProps {
  /** Subheading above the list */
  title?: string;
  documents: SpoolStepPersistedDoc[];
  /** Used when documents omit per-file timestamps (common on weld-step GET). */
  rowUpdatedAt?: string | null;
  downloadContext: {
    projectId: number;
    pipeId: number;
    spoolId: number;
    projectTypeId: number;
  } | null;
}

function subtitleForDoc(doc: SpoolStepPersistedDoc, rowUpdatedAt: string | null | undefined): string {
  const date =
    formatSpoolStepDocumentDate(doc.uploadedAt) ||
    formatSpoolStepDocumentDate(rowUpdatedAt ?? undefined);
  const parts: string[] = [];
  if (doc.uploadedBy) parts.push(doc.uploadedBy);
  if (date) parts.push(date);
  if (doc.documentCategory) parts.push(doc.documentCategory);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function SpoolStepSavedDocumentsList({
  title = 'Saved documents',
  documents,
  rowUpdatedAt,
  downloadContext,
}: SpoolStepSavedDocumentsListProps) {
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const handleDownload = useCallback(
    async (doc: SpoolStepPersistedDoc) => {
      if (!downloadContext) {
        Notification.error('Download', 'Missing project context for download.');
        return;
      }
      const key = doc.s3Key;
      setDownloadingKey(key);
      try {
        const url = await getPresignedUrlToDownloadSpoolsbaseDoc({
          s3Key: doc.s3Key,
          projectId: downloadContext.projectId,
          pipeId: downloadContext.pipeId,
          spoolId: downloadContext.spoolId,
          projectTypeId: downloadContext.projectTypeId,
        });
        if (!url || typeof url !== 'string') {
          Notification.error('Download', 'Could not open file — check permissions or try again.');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      } finally {
        setDownloadingKey(null);
      }
    },
    [downloadContext]
  );

  if (!documents.length) return null;

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-800">{title}</div>
      <div className="space-y-2">
        {documents.map((doc) => (
          <div
            key={doc.s3Key}
            className="flex items-center justify-between gap-3 rounded-lg bg-green-50 p-2 pr-2 border border-green-100"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="text-green-600 shrink-0 pt-0.5">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 break-all leading-tight" title={doc.fileName}>
                  {doc.fileName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{subtitleForDoc(doc, rowUpdatedAt)}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 h-8 w-8 p-0 text-green-700 border-green-200 hover:bg-green-100"
              disabled={!downloadContext || downloadingKey === doc.s3Key}
              title={`Download ${doc.fileName}`}
              aria-label={`Download ${doc.fileName}`}
              onClick={() => void handleDownload(doc)}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
